import { useCallback, useEffect, useMemo, useState } from "react";

import { businessApi } from "../../../../services/api";
import DatePickerInput from "../../../../components/DatePickerInput";
import ShopSelect from "../../../../components/ShopSelect";
import {
  CONVERSION_BASIS_OPTIONS, FIXED_SALARY_HINT, FIXED_SALARY_NO_TEXT, MINIMUM_WAGE_REMINDER, NEW_SALARY_COVERAGE,
  NEW_SALARY_COVERAGE_TEXT, RATE_UNDECIDED_TEXT, SALARY_COVERAGE_OPTIONS, UNPAID_DAY_TEXT, agreedDailyRateMinimum,
  centsToInput, deriveRatePreview, fixedSalaryQuestion, impliedPaidDays, inputToCents,
} from "../../../../utils/compensationMath";
import { Button, ConfirmDialog, Dialog, ErrorState, Notice, Textarea } from "../../../../components/ui";
import ScheduleFitNote from "./ScheduleFitNote";
import {
  PAY_FREQUENCIES, compensationActions, compensationError, compensationHistoryFrom, dateKey, formatPay,
  formatPayDate, formatPeso, groupCompensation, payPer, payPeriodText,
  NO_FIXED_ALLOWANCE_TEXT, allowanceCentsFor, supportsFixedAllowance,
} from "../compensationView";

const labelClass = "mb-1.5 block text-label text-secondary";
const hintClass = "mt-1.5 text-meta text-tertiary";
const errorClass = "mt-1.5 text-meta text-danger";
const inputClass = "h-control w-full rounded-control border border-default bg-surface px-3 text-body text-foreground hover:border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-focus max-md:h-touch";

const MODAL_TEXT = {
  initial: {
    title: "Set pay",
    intro: "This is what payroll pays this employee. It starts on the date you choose, even in the middle of a payroll period.",
    submit: "Save pay",
    review: (name) => `Set ${name}'s pay?`,
  },
  change: {
    title: "Change pay",
    intro: "The pay so far stays in the history and ends the day before the new pay starts.",
    submit: "Save pay change",
    review: (name) => `Change ${name}'s pay?`,
  },
  correct: {
    title: "Correct this pay record",
    intro: "Use this to fix a mistake. For a raise or any new amount from a later date, use Change pay instead.",
    submit: "Save correction",
    review: (name) => `Correct ${name}'s pay record?`,
  },
};

// Report 55 (gate 1): what a fixed salary covers, as the employment agreement states it. Package A0: required for a
// salary; "Not stated" only describes records saved before that rule.
const SALARY_COVERAGE = {
  "": "Not stated",
  ...Object.fromEntries(SALARY_COVERAGE_OPTIONS.map(({ value, label }) => [value, label])),
};

// A stored row's rate evidence (JSON text or object).
const evidenceOf = (row) => {
  const raw = row?.rate_basis_evidence;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
};

const typeLabel = (types, value) => types.find((type) => type.value === value)?.label || String(value || "").replace(/_/g, " ");

/**
 * Set, change or correct pay (C3: it decides what payroll pays). The form is filled in, then reviewed once before
 * it is saved — the review states the pay now, the new pay, the date and what payroll does with it. A refusal
 * returns to the form with the server's reason, the typed values kept.
 */
function PayFormModal({ mode, record, basis, memberId, memberName, nextChangeDate, today, hireDate, employmentTypes, defaultEmploymentType, hrSettings, onClose, onSubmit }) {
  const text = MODAL_TEXT[mode];
  const source = mode === "correct" ? record : basis;
  const [form, setForm] = useState(() => ({
    employmentType: source?.employment_type || defaultEmploymentType || employmentTypes[0]?.value || "full_time",
    baseSalary: mode === "change" ? "" : centsToInput(source?.base_salary_cents),
    salaryFrequency: source?.salary_frequency || "monthly",
    allowance: centsToInput(source?.allowance_cents) === "0" ? "" : centsToInput(source?.allowance_cents),
    // Report 54B 10A: the ordinary-day overtime rate, as a multiple of the hourly rate. The server refuses less than 1.25.
    overtimeRate: String(Number(source?.overtime_rate_multiplier || 1.25)),
    // Policy A: a salary pays the scheduled work days and paid holidays. Only correcting a record that already pays every
    // day may keep that.
    salaryDayCoverage: mode === "correct" && source?.salary_day_coverage === "EVERY_DAY" ? "EVERY_DAY" : NEW_SALARY_COVERAGE,
    // Report 66 D3: HR's answer, "yes" / "no". A correction starts from HR's earlier "yes"; nothing is assumed for older records.
    fixedSalary: mode === "correct" && evidenceOf(source).fixedSalaryConfirmed === true ? "yes" : "",
    // Report 65: how a scheduled-days salary converts to a daily rate, as its agreement states it. A correction starts
    // from the recorded basis; a pay change restates it (a new salary needs its own).
    conversionBasis: mode === "correct" && ["SALARY_DOLE_FACTOR", "SALARY_AGREED_DAILY"].includes(source?.rate_basis) ? (evidenceOf(source).method || "") : "",
    agreedDailyRate: mode === "correct" && source?.rate_basis === "SALARY_AGREED_DAILY" ? centsToInput(source?.daily_rate_cents) : "",
    specialDaysPaid: mode === "correct" && source?.rate_basis === "SALARY_AGREED_DAILY" && typeof evidenceOf(source).specialDaysPaid === "boolean"
      ? String(evidenceOf(source).specialDaysPaid) : "",
    effectiveFrom: mode === "correct" ? dateKey(record?.effective_from) : mode === "change" ? (nextChangeDate || "") : (dateKey(hireDate) || today || ""),
    notes: mode === "correct" ? record?.notes || "" : "",
    // Report 70: a rate far from what the week suggests is kept with a reason and the agreement reference.
    rateReviewReason: mode === "correct" ? evidenceOf(source).rateReview?.reason || "" : "",
    agreementRef: mode === "correct" ? evidenceOf(source).rateReview?.agreementRef || "" : "",
  }));
  const [fit, setFit] = useState(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [step, setStep] = useState("edit");
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const per = payPer(form.salaryFrequency);
  // Daily and hourly pay has no fixed allowance (owner decision A1); the field is not offered and nothing is sent.
  const variablePay = !supportsFixedAllowance(form.salaryFrequency);
  const scheduledDays = !variablePay && form.salaryDayCoverage === "SCHEDULED_WORK_DAYS";
  const keepsEveryDay = mode === "correct" && record?.salary_day_coverage === "EVERY_DAY";
  const statesDailyRate = scheduledDays && form.conversionBasis === "AGREED_DAILY_RATE";
  const rates = deriveRatePreview(form.baseSalary, form.salaryFrequency, {
    employmentType: form.employmentType,
    salaryDayCoverage: variablePay ? null : form.salaryDayCoverage,
    conversionBasis: scheduledDays ? form.conversionBasis : null,
    agreedDailyRateInput: statesDailyRate ? form.agreedDailyRate : "",
  });
  const dailyMinimum = statesDailyRate ? agreedDailyRateMinimum(inputToCents(form.baseSalary) || 0, form.salaryFrequency) : null;
  const paidDays = !variablePay && !rates.rateUndecided ? impliedPaidDays(inputToCents(form.baseSalary) || 0, form.salaryFrequency, rates.dailyRateCents) : null;
  const datedChange = mode === "change" || (mode === "correct" && record?.change_type === "change");

  // Report 70: does this pay fit the employee's weekly schedule on the start date? Decision support only.
  const fitKey = JSON.stringify([memberId, form.baseSalary, form.salaryFrequency, form.salaryDayCoverage, form.conversionBasis, form.agreedDailyRate, form.specialDaysPaid, form.effectiveFrom, form.employmentType]);
  useEffect(() => {
    const baseSalaryCents = inputToCents(form.baseSalary);
    if (!memberId || !baseSalaryCents || baseSalaryCents <= 0) { setFit(null); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setFitLoading(true);
      businessApi.previewScheduleFit({
        business_member_id: memberId,
        effective_from: form.effectiveFrom || undefined,
        base_salary_cents: baseSalaryCents,
        salary_frequency: form.salaryFrequency,
        employment_type: form.employmentType,
        salary_day_coverage: variablePay ? undefined : form.salaryDayCoverage,
        salary_conversion_basis: scheduledDays ? form.conversionBasis || undefined : undefined,
        agreed_daily_rate_cents: statesDailyRate ? inputToCents(form.agreedDailyRate) || undefined : undefined,
        special_days_paid: statesDailyRate && form.specialDaysPaid ? form.specialDaysPaid === "true" : undefined,
        fixed_salary_confirmed: true,
      }, { signal: controller.signal })
        .then((res) => setFit(res?.fit || null))
        .catch(() => { if (!controller.signal.aborted) setFit(null); })
        .finally(() => { if (!controller.signal.aborted) setFitLoading(false); });
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
    // fitKey carries every input the check reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);
  const [rateReviewAsked, setRateReviewAsked] = useState(false);
  const needsRateReview = statesDailyRate && (rateReviewAsked || fit?.termsError?.code === "AGREED_DAILY_RATE_REVIEW_REQUIRED" || Boolean(form.rateReviewReason || form.agreementRef));
  const name = memberName || "this employee";

  const review = (event) => {
    event.preventDefault();
    if (saving) return;
    const baseSalaryCents = inputToCents(form.baseSalary);
    const allowance = allowanceCentsFor(form.allowance, form.salaryFrequency);
    const nextErrors = {};
    if (!baseSalaryCents || baseSalaryCents <= 0) nextErrors.baseSalary = "Enter the base pay (more than ₱0).";
    if (allowance.error) nextErrors.allowance = "Allowance must be ₱0 or more.";
    if (!form.effectiveFrom) nextErrors.effectiveFrom = "Choose the date this pay starts.";
    const overtimeRate = Number(form.overtimeRate);
    // Report 55 (OD-29): 1.25 is the statutory minimum; there is no pay ceiling (10 only guards a typing slip).
    if (!Number.isFinite(overtimeRate) || overtimeRate < 1.25 || overtimeRate > 10) nextErrors.overtimeRate = "Enter a rate of at least 1.25 (the legal minimum) and at most 10.";
    // Package A0: the server refuses the same (salary-terms.js); this only saves a round trip.
    if (!variablePay && !form.salaryDayCoverage) nextErrors.salaryDayCoverage = "Choose what the salary pays for.";
    if (!variablePay && form.fixedSalary === "no") nextErrors.fixedSalary = "Not saved: an amount that is not fixed is recorded as daily or hourly pay.";
    else if (!variablePay && mode !== "correct" && !form.fixedSalary) nextErrors.fixedSalary = "Say whether this is a fixed salary.";
    if (scheduledDays && !form.conversionBasis) nextErrors.conversionBasis = "Choose how the agreement sets the daily rate.";
    const agreedDailyCents = statesDailyRate ? inputToCents(form.agreedDailyRate) : null;
    if (statesDailyRate) {
      const minimum = agreedDailyRateMinimum(baseSalaryCents || 0, form.salaryFrequency);
      if (!agreedDailyCents || agreedDailyCents <= 0) nextErrors.agreedDailyRate = "Enter the agreed daily rate (more than ₱0).";
      else if (minimum && agreedDailyCents < minimum) nextErrors.agreedDailyRate = `That would mean the salary pays more days than a year has. For this salary it is at least ${formatPeso(minimum)}.`;
      if (!form.specialDaysPaid) nextErrors.specialDaysPaid = "Say whether the salary pays unworked special non-working days.";
      if (needsRateReview && form.rateReviewReason.trim().length < 10) nextErrors.rateReviewReason = "Say why the agreement states this rate (at least 10 characters).";
      if (needsRateReview && form.agreementRef.trim().length < 3) nextErrors.agreementRef = "Give the agreement reference (the contract clause or document).";
    }
    setErrors(nextErrors);
    setFailure(null);
    if (Object.keys(nextErrors).length) {
      // Take the person to the first field that needs them (its message is right beside it).
      const formElement = event.currentTarget;
      setTimeout(() => formElement?.querySelector?.('[aria-invalid="true"]')?.focus(), 0);
      return;
    }
    setPayload({
      employment_type: form.employmentType,
      salary_frequency: form.salaryFrequency,
      base_salary_cents: baseSalaryCents,
      allowance_cents: allowance.cents,
      overtime_rate_multiplier: overtimeRate,
      ...(variablePay ? {} : { salary_day_coverage: form.salaryDayCoverage || null }),
      ...(!variablePay && form.fixedSalary === "yes" ? { fixed_salary_confirmed: true } : {}),
      ...(scheduledDays ? { salary_conversion_basis: form.conversionBasis } : {}),
      ...(statesDailyRate ? { agreed_daily_rate_cents: agreedDailyCents, special_days_paid: form.specialDaysPaid === "true" } : {}),
      ...(statesDailyRate && needsRateReview ? { rate_review_reason: form.rateReviewReason.trim(), agreement_ref: form.agreementRef.trim() } : {}),
      effective_from: form.effectiveFrom,
      notes: form.notes.trim() || null,
    });
    setStep("review");
  };

  const save = async () => {
    if (saving || !payload) return;
    setSaving(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      if (err?.serverCode === "AGREED_DAILY_RATE_REVIEW_REQUIRED") setRateReviewAsked(true);
      setFailure(compensationError(err));
      setStep("edit");
      setSaving(false);
    }
  };

  const facts = payload ? [
    ...(mode === "change" && basis ? [["Pay now", formatPay(basis.base_salary_cents, basis.salary_frequency)]] : []),
    ...(mode === "correct" && record ? [["Recorded as", formatPay(record.base_salary_cents, record.salary_frequency)]] : []),
    [mode === "change" ? "New pay" : "Pay", formatPay(payload.base_salary_cents, payload.salary_frequency)],
    ...(supportsFixedAllowance(payload.salary_frequency) ? [["Fixed allowance", payload.allowance_cents > 0 ? formatPay(payload.allowance_cents, payload.salary_frequency) : "None"]] : []),
    ["Ordinary-day overtime rate", Number(payload.overtime_rate_multiplier) > 1.25 ? `${payload.overtime_rate_multiplier} × hourly rate (more favourable than the law)` : "1.25 × hourly rate (the legal minimum)"],
    ...(payload.fixed_salary_confirmed ? [["Fixed salary", "Yes, as HR states it: the same agreed amount every full period, before deductions"]] : []),
    ...(supportsFixedAllowance(payload.salary_frequency) ? [["The salary pays for", SALARY_COVERAGE[payload.salary_day_coverage || ""]]] : []),
    ...(payload.salary_conversion_basis ? [["Daily rate set by", CONVERSION_BASIS_OPTIONS.find((option) => option.value === payload.salary_conversion_basis)?.label]] : []),
    ...(payload.salary_conversion_basis === "AGREED_DAILY_RATE" ? [["Unworked special days", payload.special_days_paid ? "Paid by the salary" : "Not paid"]] : []),
    ...(payload.agreement_ref ? [["Why this daily rate", `${payload.rate_review_reason} (${payload.agreement_ref})`]] : []),
    ...(supportsFixedAllowance(payload.salary_frequency) ? [["Daily and hourly rate", rates.rateUndecided
      ? RATE_UNDECIDED_TEXT[rates.undecidedReason]
      : `${formatPeso(rates.dailyRateCents)} a day${rates.hourlyRateCents ? `, ${formatPeso(rates.hourlyRateCents)} an hour` : " (no hourly rate: part-time)"}${paidDays ? ` · the salary pays ${paidDays} days a year` : ""}`]] : []),
    ["Employment type", typeLabel(employmentTypes, payload.employment_type)],
    [mode === "change" ? "New pay starts" : "Pay starts", formatPayDate(payload.effective_from)],
  ] : [];
  const consequence = mode === "change"
    ? `From ${formatPayDate(payload?.effective_from)}, payroll pays ${name} the new amount. The pay before it stays in the history and ends the day before.`
    : mode === "correct"
      ? "Payroll has not used this record yet, so it is replaced as corrected. Pay already calculated is not changed."
      : `Payroll pays ${name} this from ${formatPayDate(payload?.effective_from)}.`;

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={step === "review" ? text.review(name) : text.title}
      description={step === "review" ? "Check the pay before it is saved." : text.intro}
    >
      <form aria-label={text.title} noValidate onSubmit={review} className="pb-2">
        {step === "review" ? (
          <div className="space-y-3 text-body-sm text-secondary">
            <dl className="divide-y divide-subtle border-y border-subtle">
              {facts.map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4 py-2 max-sm:flex-col max-sm:gap-0.5">
                  <dt>{label}</dt>
                  <dd className="font-medium text-foreground sm:text-right">{value}</dd>
                </div>
              ))}
            </dl>
            <p>{consequence}</p>
            <div className="flex flex-col-reverse gap-2 border-t border-subtle pt-4 sm:flex-row sm:justify-end">
              <Button onClick={() => setStep("edit")} disabled={saving}>Back to editing</Button>
              <Button variant="primary" loading={saving} onClick={save}>{text.submit}</Button>
            </div>
          </div>
        ) : (
          <>
            {mode === "change" && basis ? (
              <p className="mb-4 text-body-sm text-secondary">
                Pay now: <span className="font-medium text-foreground">{formatPay(basis.base_salary_cents, basis.salary_frequency)}</span>
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <span className={labelClass}>Salary is stated</span>
                <ShopSelect
                  value={form.salaryFrequency}
                  onValueChange={(value) => set("salaryFrequency", value)}
                  options={PAY_FREQUENCIES.map(({ value, label }) => ({ value, label }))}
                  ariaLabel="How the pay is stated"
                  align="start"
                  minPanelWidth={220}
                  triggerClassName="min-h-9 w-full rounded-control px-3 py-2 text-body"
                  panelClassName="py-1"
                />
                <p className={hintClass}>How the amount is written. How often it is paid comes from the employee's pay schedule.</p>
              </div>
              <div>
                <label htmlFor="comp-base" className={labelClass}>{mode === "change" ? "New base pay" : "Base pay"} (₱ {per})</label>
                <input
                  id="comp-base"
                  data-ui="input"
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  required
                  value={form.baseSalary}
                  onChange={(event) => set("baseSalary", event.target.value)}
                  aria-invalid={Boolean(errors.baseSalary)}
                  aria-describedby={errors.baseSalary ? "comp-base-error" : "comp-base-hint"}
                  className={`${inputClass} tabular-nums`}
                />
                {errors.baseSalary
                  ? <p id="comp-base-error" className={errorClass}>{errors.baseSalary}</p>
                  : (
                    <p id="comp-base-hint" className={hintClass}>
                      {variablePay
                        ? rates.workHoursPerDay
                          ? `Works out to ${formatPeso(rates.dailyRateCents)} a day and ${formatPeso(rates.hourlyRateCents)} an hour (${rates.workHoursPerDay} normal hours a day).`
                          : `The amount paid ${per}. Part-time has no set normal day, so it is not converted to a ${form.salaryFrequency === "daily" ? "rate per hour" : "rate per day"}.`
                        : `The amount paid ${per}. It stays the same when the employee's work days change.`}
                    </p>
                  )}
              </div>
              {variablePay ? (
                <div>
                  <span className={labelClass}>Fixed allowance</span>
                  <p className="rounded-control border border-dashed border-default px-3 py-2 text-body-sm text-secondary">{NO_FIXED_ALLOWANCE_TEXT}</p>
                </div>
              ) : (
                <div>
                  <label htmlFor="comp-allowance" className={labelClass}>Fixed allowance (₱ {per}, optional)</label>
                  <input
                    id="comp-allowance"
                    data-ui="input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={form.allowance}
                    onChange={(event) => set("allowance", event.target.value)}
                    aria-invalid={Boolean(errors.allowance)}
                    aria-describedby={errors.allowance ? "comp-allowance-error" : undefined}
                    className={`${inputClass} tabular-nums`}
                  />
                  {errors.allowance
                    ? <p id="comp-allowance-error" className={errorClass}>{errors.allowance}</p>
                    : <p className={hintClass}>Paid on top of base pay; leave empty for none.</p>}
                </div>
              )}
              <div>
                <label htmlFor="comp-overtime-rate" className={labelClass}>Ordinary-day overtime rate (× hourly rate)</label>
                <input
                  id="comp-overtime-rate"
                  data-ui="input"
                  type="number"
                  inputMode="decimal"
                  min="1.25"
                  max="10"
                  step="0.01"
                  value={form.overtimeRate}
                  onChange={(event) => set("overtimeRate", event.target.value)}
                  aria-invalid={Boolean(errors.overtimeRate)}
                  aria-describedby={errors.overtimeRate ? "comp-overtime-rate-error" : "comp-overtime-rate-hint"}
                  className={`${inputClass} tabular-nums`}
                />
                {errors.overtimeRate
                  ? <p id="comp-overtime-rate-error" className={errorClass}>{errors.overtimeRate}</p>
                  : <p id="comp-overtime-rate-hint" className={hintClass}>1.25 is the legal minimum. Enter more only if the agreement says so. Rest-day, holiday and night rates are in HR › Settings.</p>}
              </div>
              {variablePay ? null : (
                <fieldset className="sm:col-span-2" aria-describedby={errors.fixedSalary ? "comp-fixed-salary-error" : "comp-fixed-salary-hint"}>
                  <legend className={labelClass}>{fixedSalaryQuestion(form.salaryFrequency)}</legend>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {[["yes", "Yes, a fixed salary"], ["no", "No, it estimates a daily or hourly wage"]].map(([value, label]) => (
                      <label key={value} className="flex min-h-9 items-center gap-2 text-body text-foreground max-md:min-h-touch">
                        <input type="radio" name="comp-fixed-salary" value={value} checked={form.fixedSalary === value} onChange={() => set("fixedSalary", value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {errors.fixedSalary
                    ? <p id="comp-fixed-salary-error" className={errorClass}>{errors.fixedSalary}</p>
                    : <p id="comp-fixed-salary-hint" className={hintClass}>{FIXED_SALARY_HINT}</p>}
                  {form.fixedSalary === "no" ? (
                    <Notice className="mt-2" title="Record the agreed rate instead">
                      <p>{FIXED_SALARY_NO_TEXT}</p>
                      <Button size="sm" variant="secondary" className="mt-2" onClick={() => setForm((current) => ({ ...current, salaryFrequency: "daily", baseSalary: "", allowance: "", fixedSalary: "" }))}>
                        Switch to daily pay
                      </Button>
                    </Notice>
                  ) : null}
                </fieldset>
              )}
              {variablePay ? null : !keepsEveryDay ? (
                <div>
                  <span className={labelClass}>The salary pays for</span>
                  <p className="text-body-sm text-secondary">{NEW_SALARY_COVERAGE_TEXT}</p>
                </div>
              ) : (
                <div>
                  <label htmlFor="comp-salary-coverage" className={labelClass}>The salary pays for</label>
                  <select
                    id="comp-salary-coverage"
                    data-ui="input"
                    required
                    value={form.salaryDayCoverage}
                    onChange={(event) => set("salaryDayCoverage", event.target.value)}
                    aria-invalid={Boolean(errors.salaryDayCoverage)}
                    aria-describedby={errors.salaryDayCoverage ? "comp-salary-coverage-error" : "comp-salary-coverage-hint"}
                    className={inputClass}
                  >
                    <option value="" disabled>Choose…</option>
                    {SALARY_COVERAGE_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  {errors.salaryDayCoverage
                    ? <p id="comp-salary-coverage-error" className={errorClass}>{errors.salaryDayCoverage}</p>
                    : <p id="comp-salary-coverage-hint" className={hintClass}>This record was saved before new salaries were limited to the scheduled work days. Keep it to fix a mistake in it; a new agreement pays the scheduled work days.</p>}
                </div>
              )}
              {scheduledDays ? (
                <div>
                  <label htmlFor="comp-conversion-basis" className={labelClass}>The daily rate is set by</label>
                  <select
                    id="comp-conversion-basis"
                    data-ui="input"
                    required
                    value={form.conversionBasis}
                    onChange={(event) => set("conversionBasis", event.target.value)}
                    aria-invalid={Boolean(errors.conversionBasis)}
                    aria-describedby={errors.conversionBasis ? "comp-conversion-basis-error" : "comp-conversion-basis-hint"}
                    className={inputClass}
                  >
                    <option value="" disabled>Choose…</option>
                    {CONVERSION_BASIS_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  {errors.conversionBasis
                    ? <p id="comp-conversion-basis-error" className={errorClass}>{errors.conversionBasis}</p>
                    : <p id="comp-conversion-basis-hint" className={hintClass}>As the employment agreement states it: the days a year the salary pays. The check below compares it with the schedule.</p>}
                </div>
              ) : null}
              {statesDailyRate ? (
                <>
                  <div>
                    <label htmlFor="comp-agreed-daily" className={labelClass}>Agreed daily rate (₱)</label>
                    <input
                      id="comp-agreed-daily"
                      data-ui="input"
                      type="number"
                      inputMode="decimal"
                      min="0.01"
                      step="0.01"
                      required
                      value={form.agreedDailyRate}
                      onChange={(event) => set("agreedDailyRate", event.target.value)}
                      aria-invalid={Boolean(errors.agreedDailyRate)}
                      aria-describedby={errors.agreedDailyRate ? "comp-agreed-daily-error" : "comp-agreed-daily-hint"}
                      className={`${inputClass} tabular-nums`}
                    />
                    {errors.agreedDailyRate
                      ? <p id="comp-agreed-daily-error" className={errorClass}>{errors.agreedDailyRate}</p>
                      : <p id="comp-agreed-daily-hint" className={hintClass}>It prices overtime and holiday or rest-day work.{dailyMinimum ? ` For this salary it is at least ${formatPeso(dailyMinimum)}.` : ""}</p>}
                  </div>
                  <div>
                    <label htmlFor="comp-special-days" className={labelClass}>Unworked special non-working days</label>
                    <select
                      id="comp-special-days"
                      data-ui="input"
                      required
                      value={form.specialDaysPaid}
                      onChange={(event) => set("specialDaysPaid", event.target.value)}
                      aria-invalid={Boolean(errors.specialDaysPaid)}
                      aria-describedby={errors.specialDaysPaid ? "comp-special-days-error" : undefined}
                      className={inputClass}
                    >
                      <option value="" disabled>Choose…</option>
                      <option value="true">Paid by the salary</option>
                      <option value="false">Not paid (no work, no pay)</option>
                    </select>
                    {errors.specialDaysPaid ? <p id="comp-special-days-error" className={errorClass}>{errors.specialDaysPaid}</p> : null}
                  </div>
                </>
              ) : null}
              {variablePay ? null : (
                <div className="sm:col-span-2">
                  <span className={labelClass}>Daily and hourly rate</span>
                  <p className="text-body-sm text-secondary" aria-live="polite">
                    {rates.rateUndecided
                      ? RATE_UNDECIDED_TEXT[rates.undecidedReason]
                      : `${formatPeso(rates.dailyRateCents)} a day${rates.hourlyRateCents ? ` and ${formatPeso(rates.hourlyRateCents)} an hour (${rates.workHoursPerDay} normal hours)` : "; no hourly rate (part-time has no set normal day)"}${rates.factor ? ` — the year's salary ÷ ${rates.factor}` : paidDays ? ` — the salary pays ${paidDays} days a year` : ""}.`}
                  </p>
                  <p className={hintClass}>{UNPAID_DAY_TEXT}</p>
                </div>
              )}
              {memberId || needsRateReview ? (
                <section aria-labelledby="comp-fit-title" className="rounded-panel border border-default bg-surface-sunken p-3 sm:col-span-2" data-testid="change-pay-fit">
                  <h3 id="comp-fit-title" className="text-label text-secondary">Pay agreement check</h3>
                  {memberId && !fit && !fitLoading ? (
                    <p className="mt-1 text-body-sm text-secondary">Runs once the pay and how the daily rate is set are filled in.</p>
                  ) : null}
                  {memberId ? <ScheduleFitNote className="mt-1" fit={fit} loading={fitLoading && !fit} /> : null}
                  {needsRateReview ? (
                    <div className="mt-3 grid gap-4 border-t border-subtle pt-3 sm:grid-cols-2">
                      <p className="text-body-sm text-foreground sm:col-span-2">To save this daily rate, record why the agreement states it and where. It is kept exactly as entered.</p>
                      <div>
                        <label htmlFor="comp-rate-review" className={labelClass}>Why the agreement states this daily rate</label>
                        <input id="comp-rate-review" data-ui="input" maxLength={500} value={form.rateReviewReason} onChange={(event) => set("rateReviewReason", event.target.value)}
                          aria-invalid={Boolean(errors.rateReviewReason)} aria-describedby={errors.rateReviewReason ? "comp-rate-review-error" : "comp-rate-review-hint"} className={inputClass} placeholder="For example: agreed day rate for event work" />
                        {errors.rateReviewReason
                          ? <p id="comp-rate-review-error" className={errorClass}>{errors.rateReviewReason}</p>
                          : <p id="comp-rate-review-hint" className={hintClass}>At least 10 characters.</p>}
                      </div>
                      <div>
                        <label htmlFor="comp-agreement-ref" className={labelClass}>Agreement reference</label>
                        <input id="comp-agreement-ref" data-ui="input" maxLength={255} value={form.agreementRef} onChange={(event) => set("agreementRef", event.target.value)}
                          aria-invalid={Boolean(errors.agreementRef)} aria-describedby={errors.agreementRef ? "comp-agreement-ref-error" : undefined} className={inputClass} placeholder="For example: Contract §4.2" />
                        {errors.agreementRef ? <p id="comp-agreement-ref-error" className={errorClass}>{errors.agreementRef}</p> : null}
                      </div>
                    </div>
                  ) : fit && fit.verdict !== "ok" ? (
                    <p className="mt-2 text-meta text-tertiary">Advice only: it does not stop you saving, and the rate is worked out exactly as shown above.</p>
                  ) : null}
                </section>
              ) : null}
              <div>
                <span className={labelClass}>Employment type</span>
                <ShopSelect
                  value={form.employmentType}
                  onValueChange={(value) => set("employmentType", value)}
                  options={employmentTypes}
                  ariaLabel="Employment type"
                  align="start"
                  minPanelWidth={220}
                  triggerClassName="min-h-9 w-full rounded-control px-3 py-2 text-body"
                  panelClassName="py-1"
                />
              </div>
              <div className="sm:col-span-2">
                <span className={labelClass}>{mode === "change" ? "New pay starts on" : "Pay starts on"}</span>
                <DatePickerInput
                  required
                  value={form.effectiveFrom}
                  onValueChange={(value) => set("effectiveFrom", value)}
                  title="Select start date"
                  ariaLabel="Pay starts on"
                />
                {errors.effectiveFrom
                  ? <p className={errorClass}>{errors.effectiveFrom}</p>
                  : datedChange ? (
                    <p className={hintClass}>
                      A pay change starts on the first day of a payroll period, so one period is never paid at two rates.
                      {nextChangeDate ? ` The next period starts on ${formatPayDate(nextChangeDate)}.` : ""}
                    </p>
                  ) : <p className={hintClass}>Usually the employee&apos;s first working day.</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="comp-notes" className={labelClass}>Notes (optional)</label>
                <Textarea id="comp-notes" rows={2} maxLength={1000} value={form.notes} onChange={(event) => set("notes", event.target.value)} />
              </div>
              <p className={`${hintClass} sm:col-span-2`}>{MINIMUM_WAGE_REMINDER}</p>
            </div>

            {failure ? (
              <div role="alert" className="mt-4 rounded-panel border border-danger bg-danger-subtle px-4 py-3 text-body-sm text-danger">
                <p>{failure.message}</p>
                {failure.suggestedDate ? (
                  <Button size="sm" className="mt-2" onClick={() => { set("effectiveFrom", failure.suggestedDate); setFailure(null); }}>
                    Use {formatPayDate(failure.suggestedDate)}
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="sticky -bottom-3 -mx-6 -mb-3 mt-5 flex flex-col-reverse gap-2 border-t border-subtle bg-surface-raised px-6 py-3 sm:flex-row sm:justify-end">
              <Button onClick={onClose}>Close</Button>
              <Button type="submit" variant="primary">{text.submit}</Button>
            </div>
          </>
        )}
      </form>
    </Dialog>
  );
}

/**
 * A salary's stored daily / hourly rate and what set it, so HR can check the record payroll uses (Policy A). The rate
 * prices overtime and holiday pay; unpaid days are valued from the cutoff (APP-HR-060), never from it.
 */
function salaryRateText(record) {
  if (!supportsFixedAllowance(record.salary_frequency)) return null;
  if (record.daily_rate_cents == null) return "Daily rate not set: correct this record to say how the agreement sets it.";
  const evidence = evidenceOf(record);
  const basis = record.rate_basis === "MONTHLY_PAID_365" ? "÷ 365 (older every-day agreement)"
    : CONVERSION_BASIS_OPTIONS.find((option) => option.value === evidence.method)?.label
      || (record.rate_basis === "SALARY_AGREED_DAILY" ? "An agreed daily rate" : null);
  return [
    `Daily rate ${formatPeso(record.daily_rate_cents)}`,
    record.hourly_rate_cents != null ? `${formatPeso(record.hourly_rate_cents)} an hour` : null,
    basis ? `set by ${basis}` : null,
    evidence.fixedSalaryConfirmed === true ? "HR confirmed a fixed salary" : null,
  ].filter(Boolean).join(" · ");
}

function PayRecord({ record, label, actions, onCorrect, onCancel, busy }) {
  const rateText = salaryRateText(record);
  return (
    <li className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="text-meta text-secondary">{label}</div>
        <div className="text-panel-title tabular-nums text-foreground">{formatPay(record.base_salary_cents, record.salary_frequency)}</div>
        <div className="mt-0.5 text-body-sm text-secondary">
          {payPeriodText(record)}
          {supportsFixedAllowance(record.salary_frequency) && Number(record.allowance_cents) > 0 ? ` · Allowance ${formatPay(record.allowance_cents, record.salary_frequency)}` : ""}
          {record.employment_type ? ` · ${String(record.employment_type).replace(/_/g, " ")}` : ""}
        </div>
        {rateText ? <div className="mt-0.5 text-meta text-secondary tabular-nums">{rateText}</div> : null}
        {record.notes ? <div className="mt-0.5 text-meta text-tertiary">Notes: {record.notes}</div> : null}
        {actions.lockedReason && record.state !== "past" ? <p className="mt-1 text-meta text-tertiary">{actions.lockedReason}</p> : null}
      </div>
      {actions.correct || actions.cancel ? (
        <div className="flex shrink-0 flex-wrap gap-2">
          {actions.correct ? <Button size="sm" disabled={busy} onClick={() => onCorrect(record)}>Correct</Button> : null}
          {actions.cancel ? <Button size="sm" disabled={busy} onClick={() => onCancel(record)}>Cancel this change</Button> : null}
        </div>
      ) : null}
    </li>
  );
}

function PayList({ title, label, records, recordProps, labelFor }) {
  return (
    <section className="space-y-2">
      <h3 className="text-body font-semibold text-foreground">{title}</h3>
      <ul aria-label={label} className="divide-y divide-subtle rounded-panel border border-default">
        {records.map((record) => <PayRecord key={record.id} label={labelFor(record)} {...recordProps(record)} />)}
      </ul>
    </section>
  );
}

/**
 * HR → Employee → Pay: the pay payroll uses, upcoming pay changes, and the history. Pay changes are new dated
 * records; a record payroll has used is shown as it was paid.
 */
export default function EmployeeCompensationPanel({
  memberId, memberName, canManage, isPreActivation, showPreActivationNote, employmentTypes, defaultEmploymentType, hireDate,
  hrSettings, onChanged, showToast,
}) {
  const [history, setHistory] = useState({ records: [], today: null, nextChangeDate: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setHistory(compensationHistoryFrom(await businessApi.getCompensationHistory(memberId)));
    } catch (err) {
      setHistory({ records: [], today: null, nextChangeDate: null });
      setLoadError(compensationError(err).message);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupCompensation(history.records), [history.records]);
  const latest = groups.scheduled[groups.scheduled.length - 1] || groups.current || groups.past[0] || null;
  const canEdit = canManage && !isPreActivation;
  const hasPay = history.records.length > 0;

  const afterSave = async (message) => {
    setModal(null);
    showToast?.("success", message);
    await Promise.all([load(), onChanged?.()]);
  };

  const submit = async (payload) => {
    if (modal.mode === "correct") {
      await businessApi.updateCompensation(memberId, modal.record.id, payload);
      await afterSave("Pay record corrected.");
    } else {
      await businessApi.setCompensation(memberId, payload);
      await afterSave(modal.mode === "change" ? `Pay change saved. It starts on ${formatPayDate(payload.effective_from)}.` : "Pay saved.");
    }
  };

  const cancelChange = async () => {
    try {
      await businessApi.cancelCompensationChange(memberId, cancelling.id);
    } catch (err) {
      throw Object.assign(new Error(compensationError(err).message), { serverMessage: compensationError(err).message });
    }
    setCancelling(null);
    showToast?.("success", "Pay change cancelled.");
    await Promise.all([load(), onChanged?.()]);
  };

  const recordProps = (record) => ({
    record,
    actions: compensationActions(record, canEdit),
    onCorrect: (target) => setModal({ mode: "correct", record: target }),
    onCancel: setCancelling,
    busy: Boolean(cancelling),
  });

  return (
    <div className="space-y-5">
      {canEdit ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-body-sm text-secondary">
            {hasPay ? "A raise or any new amount is a pay change from a date. Earlier pay stays in the history." : "Payroll skips this employee until their pay is set."}
          </p>
          <Button variant="primary" disabled={loading} onClick={() => setModal({ mode: hasPay ? "change" : "initial" })}>
            {hasPay ? "Change pay" : "Set pay"}
          </Button>
        </div>
      ) : showPreActivationNote ? (
        <Notice>Pay is set when someone is hired through Recruitment. End this old onboarding, then hire them through Recruitment › Direct hire.</Notice>
      ) : null}

      {loading ? (
        <p className="text-body-sm text-tertiary">Loading pay…</p>
      ) : loadError ? (
        <ErrorState title="Pay did not load" description={loadError} onRetry={load} />
      ) : !hasPay ? (
        <p className="rounded-panel border border-dashed border-default px-4 py-8 text-center text-body-sm text-secondary">No pay is set for this employee yet.</p>
      ) : (
        <div className="space-y-5">
          {groups.current ? (
            <PayList title="Current pay" label="Current pay" records={[groups.current]} recordProps={recordProps} labelFor={() => "Paid now"} />
          ) : (
            <p className="text-body-sm text-secondary">No pay applies today.</p>
          )}
          {groups.scheduled.length ? (
            <PayList
              title="Upcoming pay changes"
              label="Upcoming pay changes"
              records={groups.scheduled}
              recordProps={recordProps}
              labelFor={(record) => (record.change_type === "change" ? "Scheduled change" : "Starts later")}
            />
          ) : null}
          {groups.past.length ? (
            <PayList title="Pay history" label="Pay history" records={groups.past} recordProps={recordProps} labelFor={() => "Earlier pay"} />
          ) : null}
        </div>
      )}

      {modal ? (
        <PayFormModal
          key={`${modal.mode}-${modal.record?.id || "new"}`}
          mode={modal.mode}
          record={modal.record || null}
          basis={latest}
          memberId={memberId}
          memberName={memberName}
          nextChangeDate={history.nextChangeDate}
          today={history.today}
          hireDate={hireDate}
          employmentTypes={employmentTypes}
          defaultEmploymentType={defaultEmploymentType}
          hrSettings={hrSettings}
          onClose={() => setModal(null)}
          onSubmit={submit}
        />
      ) : null}
      {cancelling ? (
        <ConfirmDialog
          title={`Cancel the pay change from ${formatPayDate(cancelling.effective_from)}?`}
          facts={[
            { label: "Change", value: formatPay(cancelling.base_salary_cents, cancelling.salary_frequency) },
            ...(groups.current ? [{ label: "Pay now", value: formatPay(groups.current.base_salary_cents, groups.current.salary_frequency) }] : []),
          ]}
          consequence="The pay before it continues."
          confirmLabel="Cancel pay change"
          cancelLabel="Keep it"
          tone="danger"
          onConfirm={cancelChange}
          onClose={() => setCancelling(null)}
        />
      ) : null}
    </div>
  );
}
